<template>
  <div class="hub-page">
    <section class="hub-hero">
      <div class="hub-inner">
        <div class="hub-breadcrumb">
          <router-link :to="homePath">{{ hubText('home') }}</router-link>
          <span>/</span>
          <router-link v-if="currentCategory !== 'all'" :to="hubPath">{{ hubText('blog') }}</router-link>
          <span v-else>{{ hubText('blog') }}</span>
          <template v-if="currentCategory !== 'all'">
            <span>/</span>
            <span>{{ currentCategoryObj.label }}</span>
          </template>
        </div>
        <div class="hub-hero-main">
          <span class="hub-kicker">{{ hubText('blogName') }}</span>
          <h1>{{ pageTitle }}</h1>
          <p class="hub-hero-desc">{{ pageDescription }}</p>
        </div>
      </div>
    </section>

    <section class="hub-content">
      <div class="hub-inner">
        <div class="hub-layout">
          <aside class="hub-sidebar">
            <div v-if="featuredArticles.length" class="hub-side-box hub-side-featured">
              <h2>{{ hubText('featuredReading') }}</h2>
              <router-link
                v-for="item in featuredArticles"
                :key="'feat-' + item.slug"
                :to="articlePath(item.slug)"
              >
                <strong>{{ item.title }}</strong>
                <span>{{ hubText('blogName') }} · {{ categoryLabel(item.category) }}</span>
              </router-link>
            </div>
            <div class="hub-side-box">
              <h2>{{ hubText('featureEntry') }}</h2>
              <h3 class="hub-side-group-label">{{ hubText('aiImageModels') }}</h3>
              <router-link
                v-for="item in modelLinks"
                :key="'model-' + item.path"
                :to="localizedPath(item.path)"
              >{{ item.label }}</router-link>
              <h3 class="hub-side-group-label">{{ hubText('aiImageTools') }}</h3>
              <router-link
                v-for="item in toolLinks"
                :key="'tool-' + item.path"
                :to="localizedPath(item.path)"
              >{{ item.label }}</router-link>
            </div>
          </aside>

          <div class="hub-main-column">
            <nav class="hub-tabs" :aria-label="hubText('categoryNavLabel')">
              <router-link
                v-for="cat in navCategories"
                :key="'cat-' + cat.key"
                :to="categoryRoute(cat.key)"
                :class="{ active: currentCategory === cat.key }"
              >{{ cat.label }}</router-link>
            </nav>
            <div class="hub-search-bar">
              <label class="hub-search-label" for="hub-search-input">{{ hubText('searchLabel') }}</label>
              <div class="hub-search-row">
                <input
                  id="hub-search-input"
                  v-model="searchDraft"
                  type="search"
                  class="hub-search-input"
                  :placeholder="hubText('searchPlaceholder')"
                  @keyup.enter="applySearch"
                >
                <button type="button" class="hub-search-btn" @click="applySearch">{{ hubText('searchButton') }}</button>
                <button v-if="searchQuery" type="button" class="hub-search-clear" @click="clearSearch">{{ hubText('clearButton') }}</button>
              </div>
              <p v-if="searchQuery" class="hub-search-meta">
                {{ searchResultText }}
              </p>
              <div v-if="!searchQuery && searchSuggestions.length" class="hub-search-suggestions">
                <span class="hub-search-suggestions-label">{{ hubText('popularSearches') }}</span>
                <router-link
                  v-for="term in searchSuggestions"
                  :key="'sug-' + term"
                  :to="searchRoute(term)"
                  class="hub-search-chip"
                >{{ term }}</router-link>
              </div>
            </div>
            <div class="hub-main-list">
              <article v-for="article in paginatedArticles" :key="article.slug" class="hub-card">
                <div class="hub-card-meta">
                  <span v-if="article.badge === 'Hot'" class="hub-card-hot" aria-label="Hot">
                    <span class="hub-card-hot-icon" aria-hidden="true">🔥</span>
                    <span>Hot</span>
                  </span>
                  <span class="hub-card-source">{{ hubText('blogName') }}</span>
                  <router-link :to="categoryPath(article.category)" class="hub-card-category">{{ categoryLabel(article.category) }}</router-link>
                </div>
                <h2>
                  <router-link :to="articlePath(article.slug)">{{ article.title }}</router-link>
                </h2>
                <p>{{ article.excerpt }}</p>
                <div class="hub-card-actions">
                  <router-link :to="articlePath(article.slug)" class="hub-read-link">{{ hubText('readArticle') }}</router-link>
                  <router-link :to="localizedPath(article.target.path)" class="hub-target-link">{{ article.target.label }}</router-link>
                </div>
              </article>
              <div v-if="!filteredArticles.length" class="hub-empty">
                <p>{{ noResultsText }}</p>
                <button type="button" class="hub-search-btn" @click="clearSearch">{{ hubText('viewAllArticles') }}</button>
              </div>
            </div>
            <nav v-if="totalPages > 1" class="hub-pagination" :aria-label="hubText('paginationLabel')">
              <router-link
                v-if="currentPage > 1"
                :to="pageRoute(currentPage - 1)"
                class="hub-page-link hub-page-prev"
              >{{ hubText('previousPage') }}</router-link>
              <router-link
                v-for="page in paginationPages"
                :key="'page-' + page"
                :to="pageRoute(page)"
                class="hub-page-link"
                :class="{ active: currentPage === page }"
              >{{ page }}</router-link>
              <router-link
                v-if="currentPage < totalPages"
                :to="pageRoute(currentPage + 1)"
                class="hub-page-link hub-page-next"
              >{{ hubText('nextPage') }}</router-link>
            </nav>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
var hubConfig = require('../config/hub')

module.exports = {
  name: 'Hub',
  data: function () {
    return {
      searchDraft: ''
    }
  },
  watch: {
    '$route.query.q': {
      immediate: true,
      handler: function (value) {
        this.searchDraft = value || ''
      }
    }
  },
  computed: {
    categories: function () {
      return hubConfig.getHubCategories(this.currentHubLocale)
    },
    navCategories: function () {
      var self = this
      var categories = this.categories.filter(function (cat) {
        if (cat.key === 'all') return true
        return self.categoryCount(cat.key) > 0
      })
      var insightIndex = -1
      var i
      for (i = 0; i < categories.length; i++) {
        if (categories[i].key === 'ai-model-insights') {
          insightIndex = i
          break
        }
      }
      if (insightIndex > 1) {
        categories.splice(1, 0, categories.splice(insightIndex, 1)[0])
      }
      return categories
    },
    currentCategory: function () {
      var cat = this.$route.params.category || 'all'
      return hubConfig.getHubCategory(this.currentHubLocale, cat) ? cat : 'all'
    },
    currentCategoryObj: function () {
      return hubConfig.getHubCategory(this.currentHubLocale, this.currentCategory)
    },
    articles: function () {
      return hubConfig.getHubArticles(this.currentHubLocale, this.currentCategory)
    },
    searchQuery: function () {
      return (this.$route.query.q || '').trim()
    },
    filteredArticles: function () {
      return hubConfig.searchHubArticles(this.currentHubLocale, this.searchQuery, this.currentCategory)
    },
    pageSize: function () {
      return 20
    },
    totalPages: function () {
      return Math.max(1, Math.ceil(this.filteredArticles.length / this.pageSize))
    },
    currentPage: function () {
      var page = parseInt(this.$route.query.page, 10)
      if (!page || page < 1) page = 1
      if (page > this.totalPages) page = this.totalPages
      return page
    },
    paginatedArticles: function () {
      var start = (this.currentPage - 1) * this.pageSize
      return this.filteredArticles.slice(start, start + this.pageSize)
    },
    paginationPages: function () {
      var pages = []
      var i
      for (i = 1; i <= this.totalPages; i++) {
        pages.push(i)
      }
      return pages
    },
    searchSuggestions: function () {
      return hubConfig.getHubSearchSuggestions(this.currentHubLocale, 10, this.currentCategory)
    },
    modelLinks: function () {
      return hubConfig.getCommonModelLinks(this.currentHubLocale)
    },
    toolLinks: function () {
      return hubConfig.getCommonToolLinks(this.currentHubLocale)
    },
    featuredArticles: function () {
      if (this.currentCategory !== 'all') return []
      return hubConfig.getHubFeaturedArticles(this.currentHubLocale, 'all')
    },
    pageTitle: function () {
      return this.hubText('allPageTitle')
    },
    pageDescription: function () {
      if (this.currentCategoryObj) return this.currentCategoryObj.description
      return this.hubText('defaultDescription')
    },
    homePath: function () {
      return this.routeLangPrefix ? this.routeLangPrefix : '/'
    },
    hubPath: function () {
      return this.routeLangPrefix + '/hub'
    },
    langPrefix: function () {
      if (this.routeLangPrefix) return this.routeLangPrefix
      return this.currentHubLocale === 'en' ? '' : '/' + this.currentHubLocale
    },
    routeLangPrefix: function () {
      var lang = this.$route.params.lang
      return lang ? '/' + lang : ''
    },
    currentHubLocale: function () {
      return hubConfig.normalizeHubLocale(this.$route.params.lang || hubConfig.DEFAULT_LOCALE)
    },
    canonicalPath: function () {
      var path = this.$route.path
      if (this.routeLangPrefix && (path.indexOf(this.routeLangPrefix + '/') === 0 || path === this.routeLangPrefix)) return path
      if (path.indexOf('/hub') === 0) return (this.currentHubLocale === 'en' ? '' : '/' + this.currentHubLocale) + path
      return path
    },
    searchResultText: function () {
      return this.hubText('searchResultPrefix') + this.searchQuery + this.hubText('searchResultMiddle') + this.filteredArticles.length + this.hubText('searchResultSuffix')
    },
    noResultsText: function () {
      return this.hubText('noResultsPrefix') + this.searchQuery + this.hubText('noResultsSuffix')
    }
  },
  methods: {
    hubText: function (key) {
      return hubConfig.getHubText(this.currentHubLocale, key)
    },
    categoryRoute: function (key) {
      var path = key === 'all' ? this.langPrefix + '/hub' : this.langPrefix + '/hub/' + key
      if (this.searchQuery) {
        return { path: path, query: { q: this.searchQuery } }
      }
      return path
    },
    searchRoute: function (term) {
      return {
        path: this.langPrefix + '/hub',
        query: { q: term }
      }
    },
    pageRoute: function (page) {
      var query = {}
      if (this.searchQuery) query.q = this.searchQuery
      if (page > 1) query.page = page
      return {
        path: this.$route.path,
        query: query
      }
    },
    applySearch: function () {
      var q = (this.searchDraft || '').trim()
      var query = q ? { q: q } : {}
      this.$router.replace({
        path: this.$route.path,
        query: query
      })
    },
    clearSearch: function () {
      this.searchDraft = ''
      this.$router.replace({
        path: this.$route.path,
        query: {}
      })
    },
    categoryPath: function (key) {
      if (key === 'all') return this.langPrefix + '/hub'
      return this.langPrefix + '/hub/' + key
    },
    articlePath: function (slug) {
      return this.langPrefix + '/hub/articles/' + slug
    },
    localizedPath: function (path) {
      if (path.indexOf('/zh/') === 0 || path === '/zh') return path
      if (path.indexOf('/hub/') === 0) return this.langPrefix + path
      return this.langPrefix + path
    },
    categoryLabel: function (key) {
      var cat = hubConfig.getHubCategory(this.currentHubLocale, key)
      return cat ? cat.label : key
    },
    categoryCount: function (key) {
      return hubConfig.getHubArticles(this.currentHubLocale, key).length
    }
  },
  metaInfo: function () {
    var title = this.currentCategory === 'all'
      ? this.hubText('metaTitleAll')
      : this.currentCategoryObj.label + this.hubText('metaTitleCategorySuffix')
    var desc = this.pageDescription
    var canonical = 'https://www.miralvis.com' + this.canonicalPath
    var hubPath = this.currentCategory === 'all' ? '/hub' : '/hub/' + this.currentCategory
    var alternateLinks = hubConfig.SUPPORTED_LOCALES.map(function (locale) {
      var prefix = locale === hubConfig.DEFAULT_LOCALE ? '' : '/' + locale
      return {
        vmid: 'hub-alternate-' + locale,
        rel: 'alternate',
        hreflang: locale,
        href: 'https://www.miralvis.com' + prefix + hubPath
      }
    })
    alternateLinks.unshift({ rel: 'canonical', href: canonical })
    alternateLinks.push({
      vmid: 'hub-alternate-x-default',
      rel: 'alternate',
      hreflang: 'x-default',
      href: 'https://www.miralvis.com' + hubPath
    })
    return {
      title: title,
      meta: [
        { vmid: 'description', name: 'description', content: desc },
        { vmid: 'og:title', property: 'og:title', content: title },
        { vmid: 'og:description', property: 'og:description', content: desc },
        { vmid: 'og:url', property: 'og:url', content: canonical }
      ],
      link: alternateLinks
    }
  }
}
</script>

<style scoped>
.hub-page {
  min-height: 100vh;
  background: #f7f7f8;
  color: #1a1a2e;
}
.hub-inner {
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
}
.hub-hero {
  padding: 46px 0 18px;
  background: #f7f7f8;
}
.hub-breadcrumb {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 13px;
  color: #8b8fa3;
  margin-bottom: 22px;
}
.hub-breadcrumb a:hover {
  color: #059669;
}
.hub-hero-main {
  max-width: 820px;
}
.hub-kicker {
  display: inline-flex;
  margin-bottom: 12px;
  color: #059669;
  font-size: 13px;
  font-weight: 700;
}
.hub-hero h1 {
  margin: 0 0 14px;
  font-size: 42px;
  line-height: 1.16;
  letter-spacing: 0;
}
.hub-hero-desc {
  margin: 0;
  max-width: 760px;
  color: #555;
  font-size: 17px;
  line-height: 1.7;
}
.hub-side-featured a {
  padding: 10px;
  border-top: 1px solid #f0f1f3;
  border-radius: 7px;
}
.hub-side-featured a:first-of-type {
  border-top: none;
}
.hub-side-featured a strong {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  line-height: 1.45;
}
.hub-content {
  padding: 12px 0 72px;
}
.hub-layout {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}
.hub-main-column {
  min-width: 0;
}
.hub-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  margin-bottom: 18px;
  padding-bottom: 0;
  border-bottom: 1px solid #e5e7eb;
}
.hub-tabs a {
  display: inline-flex;
  align-items: center;
  margin-bottom: -1px;
  padding: 10px 2px;
  color: #666;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  border-bottom: 2px solid transparent;
  transition: color 0.2s ease, border-color 0.2s ease;
}
.hub-tabs a:hover {
  color: #059669;
}
.hub-tabs a.active {
  color: #059669;
  border-bottom-color: #059669;
}
.hub-search-bar {
  margin-bottom: 18px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}
.hub-search-label {
  display: block;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
}
.hub-search-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.hub-search-input {
  flex: 1;
  min-width: 220px;
  min-height: 40px;
  padding: 9px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
}
.hub-search-btn,
.hub-search-clear {
  min-height: 40px;
  padding: 9px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.hub-search-btn {
  border: none;
  background: #059669;
  color: #fff;
}
.hub-search-clear {
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #555;
}
.hub-search-meta {
  margin: 10px 0 0;
  color: #666;
  font-size: 13px;
}
.hub-search-suggestions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}
.hub-search-suggestions-label {
  color: #8b8fa3;
  font-size: 12px;
  font-weight: 700;
}
.hub-search-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #555;
  font-size: 12px;
  font-weight: 600;
}
.hub-search-chip:hover {
  color: #059669;
  background: rgba(5, 150, 105, 0.08);
}
.hub-empty {
  padding: 28px 22px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  background: #fff;
  text-align: center;
}
.hub-empty p {
  margin: 0 0 14px;
  color: #666;
}
.hub-main-list {
  display: grid;
  gap: 16px;
}
.hub-pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 22px;
}
.hub-page-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 38px;
  min-height: 38px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  font-weight: 700;
}
.hub-page-link:hover,
.hub-page-link.active {
  border-color: #059669;
  background: rgba(5, 150, 105, 0.08);
  color: #059669;
}
.hub-card {
  padding: 22px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}
.hub-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 10px;
  color: #8b8fa3;
  font-size: 12px;
}
.hub-card-hot {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 24px;
  padding: 4px 9px;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.02em;
}
.hub-card-hot-icon {
  font-size: 13px;
  line-height: 1;
}
.hub-card-source,
.hub-card-category {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 4px 9px;
  border-radius: 999px;
  line-height: 1;
}
.hub-card-source {
  background: #f3f4f6;
  color: #6b7280;
  font-weight: 700;
}
.hub-card-category {
  background: rgba(5, 150, 105, 0.08);
  color: #059669;
  font-weight: 700;
}
.hub-card h2 {
  margin: 0 0 9px;
  font-size: 23px;
  line-height: 1.35;
  letter-spacing: 0;
}
.hub-card h2 a:hover {
  color: #059669;
}
.hub-card p {
  color: #555;
  font-size: 15px;
  line-height: 1.75;
  margin-bottom: 14px;
}
.hub-keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 16px;
}
.hub-keywords span {
  padding: 5px 8px;
  border-radius: 6px;
  background: #f3f4f6;
  color: #666;
  font-size: 12px;
}
.hub-card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.hub-read-link,
.hub-target-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 8px 13px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
}
.hub-read-link {
  background: #059669;
  color: #fff;
}
.hub-target-link {
  border: 1px solid #e5e7eb;
  color: #059669;
}
.hub-sidebar {
  position: sticky;
  top: 18px;
  display: grid;
  gap: 14px;
}
.hub-side-box {
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}
.hub-side-box h2 {
  margin: 0 0 12px;
  font-size: 15px;
}
.hub-side-group-label {
  margin: 12px 0 4px;
  padding: 0 10px;
  color: #8b8fa3;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.hub-side-group-label:first-of-type {
  margin-top: 0;
}
.hub-side-box a {
  display: block;
  padding: 10px;
  border-top: 1px solid #f0f1f3;
  border-radius: 7px;
  font-size: 13px;
  color: #555;
}
.hub-side-box a:first-of-type {
  border-top: none;
}
.hub-side-box a.active,
.hub-side-box a:hover {
  color: #059669;
  background: rgba(5, 150, 105, 0.06);
}
.hub-side-box strong {
  display: block;
  font-size: 13px;
  color: inherit;
}
.hub-side-box span {
  display: block;
  margin-top: 3px;
  color: #8b8fa3;
  font-size: 12px;
  line-height: 1.45;
}
@media (max-width: 980px) {
  .hub-layout {
    grid-template-columns: 1fr;
  }
  .hub-main-column {
    order: 1;
  }
  .hub-sidebar {
    order: 2;
    position: static;
  }
}
@media (max-width: 640px) {
  .hub-inner {
    padding: 0 14px;
  }
  .hub-hero {
    padding-top: 34px;
  }
  .hub-hero h1 {
    font-size: 28px;
  }
  .hub-hero-desc {
    font-size: 15px;
  }
  .hub-card {
    padding: 16px;
  }
  .hub-card h2 {
    font-size: 19px;
  }
}
</style>
